import * as React from "react";

import { Datetime } from "src/library/components/Datetime";
import {
  IInfoItem,
  InfoBar
} from "src/library/components/InfoBar";
import { PhoneNumber } from "src/library/components/PhoneNumber";
import type { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  IUser,
  IUserGroup
} from "src/core/users/api/users.api";
import AssignUserAndGroupSelect from "src/core/users/components/AssignUserAndGroupSelect";

import { IPendingCustomer } from "../api/pendingCustomer.api";
import PendingCustomerStatus from "./PendingCustomerStatus";

export type PendingCustomerSidebarUpdate = "userGroupId" | "assignedUserId";

interface IProps {
  /** controls loading status */
  isLoading: boolean;
  /** provides data to render */
  data: IPendingCustomer | null;
  /** terminology */
  terminology: ITerminology;
  /** callback to fire when some updates are issued */
  handleUpdate: (updateType: PendingCustomerSidebarUpdate, updateValue: number | null) => void;
}

/**
 * @description provides a sidebar for the pending customer edit page allowing view and update of some pending customer attributes
 */
const PendingCustomerSidebar = ({
  isLoading, data, terminology, handleUpdate
}: IProps) => {
  const [ selectedUserGroup, setSelectedUserGroup ] = React.useState<IUserGroup | null>(null);
  const [ assignedUser, setAssignedUser ] = React.useState<IUser | null>(null);

  // keep local state aligned with component consumer
  React.useEffect(() => {
    if (data?.userGroup || data?.userGroupId === null) {
      setSelectedUserGroup(data.userGroup || null);
    }
  }, [ data?.userGroupId, data?.userGroup ]);

  // keep local state aligned with component consumer
  React.useEffect(() => {
    if (data?.assignedUser || data?.assignedUserId === null) {
      setAssignedUser(data.assignedUser || null);
    }
  }, [ data?.assignedUser, data?.assignedUserId ]);

  const sidebarItems: IInfoItem[] = React.useMemo(() => [
    {
      label: "Customer status",
      value: data ? <PendingCustomerStatus status={data.status} /> : "-"
    },
    {
      label: "Contact number",
      value: data?.contactNumber ? (
        <PhoneNumber
          phoneNumber={data.contactNumber}
          linked
        />
      ) : "-"
    },
    {
      label: "Contact email",
      value: data?.contactEmail ? (
        <a href={`mailto: ${data.contactEmail}`}>
          {data.contactEmail}
        </a>
      ) : "-"
    },
    {
      label: "Created On",
      value: data ? (
        <Datetime
          date={data.createdAt}
          template="long_date"
        />
      ) : "-"
    },
    {
      label: "Updated at",
      value: data ? (
        <Datetime
          date={data.updatedAt}
          template="long_date"
        />
      ) : "-"
    }
  ], [ data ]);

  return (
    <InfoBar
      isLoading={isLoading}
      title={`${terminology.customer.singular.text} information`}
      items={sidebarItems}
    >
      <AssignUserAndGroupSelect
        selectedUserGroup={selectedUserGroup ?? null}
        assignedUser={assignedUser ?? null}
        terminology={terminology}
        handleUpdate={handleUpdate}
      />
    </InfoBar>
  );
};

export default PendingCustomerSidebar;
